/* eslint-disable @next/next/no-img-element */
import React, {
  useId,
  useEffect,
  useRef,
} from 'react';
import { mtstatTrack } from '$/utils/mtstat';
import useTrackListExposure from '$/hooks/useTrackListExposure';
import IconFont from '$/components/IconFont';
import classNames from 'classnames';
import type { V3CanvasProjectItem } from '$/domains/workFlowV3/type';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { handleImageUrl } from '$/utils/image';
import { openChooseIPModal } from '$/components/ChooseIpModal/chooseIPStore';
import { navigate } from '@/common/agents';
import type { Config } from '@/common/config';
import style from './index.module.scss';

const PROJECT_DISPLAY_COUNT = 5;
const PROJECT_CREATE_CARD_WIDTH = 253;
const PROJECT_CARD_WIDTH = 270;
const CARD_GAP = 12;
const CONTAINER_TRAILING_PADDING = 40;

type MyProjectProps = {
  width?: number;
  projects: V3CanvasProjectItem[];
  visibleProjectCount?: number;
  onProjectCountChange?: (count: number) => void;
};

type SectionHeaderProps = {
  id: string;
  title: string;
  viewAllText: string;
  href: Config['Pathname'];
  showViewAll: boolean;
};

/** 渲染快捷入口分组标题及“查看全部”导航。 */
function SectionHeader({
  id, title, viewAllText, href, showViewAll,
}: SectionHeaderProps) {
  /** 使用应用内路由打开当前版本的项目列表。 */
  const handleViewAll = () => {
    mtstatTrack('roboneo_view_all_project_click', {});
    navigate({ mode: 'router', pathname: href });
  };

  return (
    <div className={style.sectionHeader}>
      <h2 id={id} className={style.sectionTitle}>{title}</h2>
      {showViewAll && (
        <button type="button" className={style.viewAll} onClick={handleViewAll} aria-label={viewAllText}>
          <span>{viewAllText}</span>
          <IconFont name="chevronRightBlack" fontSize={14} />
        </button>
      )}
    </div>
  );
}

/** 渲染首页的最近项目快捷入口。 */
export default function MyProject({
  width, projects, visibleProjectCount, onProjectCountChange,
}: MyProjectProps) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const clientOId = useOfficialStore((state) => state.clientOId);
  const isTeamEdition = Boolean(clientOId);
  const toLogin = useOfficialStore((state) => state.toLogin);
  const navigateMap = useOfficialStore((state) => state.navigateMap);
  const viewAllText = languageStore.smartcatTranslationText((v) => v.view_all);
  const projectTitle = languageStore.smartcatTranslationText((v) => (
    isTeamEdition ? v.team_project : v.roboneo_web_my_projects
  ));
  const createCanvasText = languageStore.smartcatTranslationText((v) => v.create_new_projects);
  const defaultProjectName = languageStore.smartcatTranslationText((v) => v.new_project);

  const listRef = useRef<HTMLDivElement>(null);
  const sectionId = useId();
  const resolvedVisibleProjectCount = visibleProjectCount ?? PROJECT_DISPLAY_COUNT;
  const visibleProjects = projects.slice(0, resolvedVisibleProjectCount);
  const fallbackWidth = visibleProjects.length > 0
    ? CONTAINER_TRAILING_PADDING + PROJECT_CREATE_CARD_WIDTH + visibleProjects.length * (PROJECT_CARD_WIDTH + CARD_GAP)
    : CONTAINER_TRAILING_PADDING + PROJECT_CREATE_CARD_WIDTH;
  /** 使用项目真实身份、显示名称和封面，曝光与点击共享口径。 */
  const projectParams = (project: V3CanvasProjectItem, position: number) => ({
    project_id: String(project.project_id || project.id || ''),
    project_name: project.name?.trim() || defaultProjectName,
    position,
    media_url: project.cover_url?.trim() || '',
  });
  useTrackListExposure(listRef, 'roboneo_home_project_card_show', visibleProjects.map((project, position) => ({
    key: `${clientOId}:${project.project_id || project.id}`, params: projectParams(project, position),
  })));

  /** 从首页“我的项目”入口校验登录态，并打开角色 IP 选择弹窗。 */
  const handleCreateProject = () => {
    if (!isUserLoggedIn) {
      toLogin().catch(() => {});
      return;
    }

    openChooseIPModal(undefined, '', 'home_create_project');
    // createRoom({
    //   trackLocation: 'home_page',
    //   trackType: 'agent_teams',
    // });
  };

  /** 打开项目默认画布，并将项目、房间、画布三个独立 ID 传入 Team Studio。 */
  const handleProjectClick = (project: V3CanvasProjectItem, position: number) => {
    mtstatTrack('roboneo_home_project_card_click', projectParams(project, position));
    const projectId = String(project.project_id || project.id || '');
    const roomId = String(project.room_id || project.project_id || project.id || '');
    const canvasId = String(project.canvas_id || project.room_id || project.project_id || project.id || '');
    if (!projectId || !roomId || !canvasId) return;
    navigateMap.historyTeamFlow({
      project_id: projectId,
      room_id: roomId,
      canvas_id: canvasId,
    });
  };

  /** 将当前可展示的真实项目数量交给首页容器统一分配宽度。 */
  useEffect(() => {
    onProjectCountChange?.(projects.length);
  }, [onProjectCountChange, projects.length]);

  return (
    <section className={style.my_project_container} style={{ '--my-project-content-width': `${width ?? fallbackWidth}px` } as React.CSSProperties} aria-labelledby={`${sectionId}-title`}>
      {/* 我的项目标题 */}
      <SectionHeader
        id={`${sectionId}-title`}
        title={projectTitle}
        viewAllText={viewAllText}
        href={isTeamEdition ? '/--roboneo/team_project' : '/--roboneo/project'}
        showViewAll={projects.length > 0}
      />

      <div className={style.cardViewport}>
        {/* 新建画布 */}
        <button
          type="button"
          className={classNames(style.createCard, style.projectCreateCard)}
          onClick={handleCreateProject}
        >
          <span className={style.dotPattern} aria-hidden="true" />
          <span className={style.createIcon}>
            <IconFont name="plusFill" fontSize={20} />
          </span>
          <span className={style.createLabel}>{createCanvasText}</span>
        </button>

        {visibleProjects.length > 0 && (
          <div className={style.scrollViewport}>
            <div ref={listRef} id={`${sectionId}-list`} className={style.cardList}>
              {/* 根据剩余宽度平铺完整项目卡，展示不下的项目不渲染。 */}
              {visibleProjects.map((project, position) => {
                const projectId = String(project.project_id || project.id || project.room_id || '');
                const projectName = project.name?.trim() || defaultProjectName;
                const coverUrl = project.cover_url?.trim() || '';
                return (
                  <button
                    key={projectId}
                    type="button"
                    className={style.projectCard}
                    data-exposure-index={position}
                    onClick={() => handleProjectClick(project, position)}
                  >
                    {coverUrl
                      ? (
                        <img
                          className={style.cardCover}
                          src={handleImageUrl(coverUrl, { mode: 3, width: 270, height: 180 })}
                          alt=""
                          draggable={false}
                          crossOrigin="anonymous"
                        />
                      )
                      : <span className={style.projectCardPlaceholder} aria-hidden="true" />}
                    <span className={style.projectCardGradient} />
                    <span className={style.projectCardName}>{projectName}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
